import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * The Review client (日本-185).
 *
 * **A graded card is one round trip.** `answerCard` returns the *next* card and the counts beside
 * it rather than answering and leaving the caller to fetch what follows: the card and the progress
 * bar have to describe the same instant, and two requests can disagree about it.
 */

export interface ReviewSubdeck {
  name: string;
  /** The leaf name is what the row shows; the full name is what まだまだ names to the server. */
  fullName: string;
  due: number;
  new: number;
  /**
   * Whether taking on more new cards here would yield any — the SUPPLY, not the limit (日本-451).
   *
   * `new` is what today's allowance offers; this is whether the deck still holds unseen cards
   * behind it. The two together are the state the affordance exists for: `new === 0` and
   * `canExtend` is a deck that has run out for the day and has not run out.
   *
   * It is what lets the button be **absent rather than disabled** — a greyed control invites a
   * press that could never do anything, and 漢字 is permanently in that state.
   */
  canExtend: boolean;
}

/**
 * Today's two bars (日本-389) — the same shape wherever they are drawn.
 *
 * **Derived on the server on every read, never accumulated here.** That is what makes them survive
 * a reload, a second device and a mid-day Anki desktop session, which the bar this replaces — scoped
 * to the sitting since the last sync — could not. The client holds none of its own: it draws fills
 * from these five numbers and nothing else.
 *
 * `cardsTotal` may fall (a card was deleted or suspended) and `reviewsTotal` may rise (a failure
 * requeued). Neither ever moves the other way.
 */
export interface DayProgress {
  /** Cards that have left today — passed, and rescheduled past the cutoff. Monotonic. */
  cardsDone: number;
  cardsTotal: number;
  passes: number;
  fails: number;
  /** Presses, not cards: a new card owes its deck's whole learning ladder before it leaves today. */
  reviewsTotal: number;
}

export interface ReviewDeckOption {
  name: string;
  /** **Disjoint from `new`.** The server subtracts, because Anki's own `due_today` includes new
   *  cards and the design draws the two side by side and sums them (`110 / 110` on `7e`). */
  due: number;
  new: number;
  /** Cards here and below, suspended excluded — `6,204 cards in rotation`. */
  inRotation: number;
  /** `≈ 21 min`, or null when there is not enough review history to measure one. Null means the
   *  line is **not drawn**: a figure with no measurement behind it is worse than no figure. */
  estimatedMinutes: number | null;
  /** Today's two bars for this deck. **Per deck card, not one summed pair** (Alex, 16 Aug 2026) —
   *  the landing lists several decks and the bars are deck-scoped, so a single pair would be a
   *  number with no name on it. */
  day: DayProgress;
  subdecks: ReviewSubdeck[];
}

export interface ReviewLanding {
  decks: ReviewDeckOption[];
  dueTotal: number;
  newTotal: number;
}

export type ReviewQueueKind = "new" | "learning" | "review";

export interface ReviewCard {
  cardId: number;
  noteId: number;
  notetype: string;
  /**
   * The notetype name when this app has a layout for it, else **null**.
   *
   * Null is the loud-failure signal (Alex, 9 Aug 2026): `Kanji (Apoc)` and stock `Basic` have no
   * designed layout, so the card region names the notetype it cannot draw instead of rendering an
   * empty screen. Grading is disabled on such a card; `Card ⋯` and `Esc` still work.
   */
  layout: string | null;
  deck: string;
  queue: ReviewQueueKind;
  flag: number;
  /** Sent straight back on the grade as `expectReps`. See `answerCard`. */
  reps: number;
  tags: string[];
  /** Every card this note has. `Suspend whole note` sends the list to the same card-level endpoint
   *  Cards uses, rather than growing a note-shaped twin of it. Usually one — Genki is one template
   *  per note. */
  noteCardIds: number[];

  headword: string;
  reading: string;
  gloss: string;
  partOfSpeech: string;
  sentence: string;
  /**
   * Which run of `sentence` is the word this card is about, or `""` for none (日本-279).
   *
   * A **field on the note**, resolved server-side and stored — not something the client works out.
   * It has to be: conjugation puts the answer out of reach of a string match (躊躇った is not
   * 躊躇う), and the collection's own Anki template can only string-match, so the answer has to be
   * on the card for AnkiDroid to draw it at all.
   *
   * Always a substring of `sentence` when non-empty. `splitOnTarget` relies on that and the server
   * proves it per row, so there is nothing here to escape or validate.
   */
  sentenceTarget: string;
  /*
   * **`formation`, `example`, `related` and `notes` were here and went with 日本-519.** They were
   * `Grammar (Apoc)`'s four added rows; grammar is a reference surface now (文法) rather than a
   * card, the notetype is gone and the server no longer sends them. Recorded rather than silently
   * deleted, because a card face with a `接続` box in it is the shape somebody would otherwise
   * reintroduce from the frames.
   */
  extra: string;
  /** Media **filenames**, not markup — the client builds its own `<img>`/`<audio>` via `mediaUrl`. */
  audio: string | null;
  sentenceAudio: string | null;
  image: string | null;
  extraImage: string | null;
  /** The corpus rank for the answer face's corner (日本-366), or **null** when the card has none.
   *
   *  Null is the design rather than a missing value: ~600 of the collection's notes carry no rank,
   *  and such a card draws no corner at all — no dash, no zero, no placeholder — because at that
   *  frequency the absence has to read as normal rather than as something that failed to load. */
  frequencyRank: number | null;

  /** `10m` / `28d`. Free information printed on the buttons; there is no decision attached. */
  failLabel: string;
  passLabel: string;
}

export interface ReviewCounts {
  new: number;
  learning: number;
  review: number;
  /** This sitting's tally. The scheduler has no concept of a sitting, so it is the server's. */
  answered: number;
  remaining: number;
}

export interface ReviewPosition {
  deck: string;
  /** How long the sitting may sit untouched before it flushes and returns you to the deck list.
   *
   *  **Sent by the server, never held here.** The same number is its sync-hold deadline — a session
   *  holds the collection's syncs so `↑` can take an answer back — so a second copy on this side
   *  would drift from the one that actually governs. */
  idleMinutes: number;
  /** Whether revealing the answer plays the word's audio (日本-347).
   *
   *  **Rides here rather than being fetched**, for a weaker version of `idleMinutes`' reason: it is
   *  a synced setting the session needs on the very first card, and a second request for one
   *  boolean is a round trip the first reveal would be racing. */
  autoplayWord: boolean;
  /** **Null means the deck is finished**, and it is the only thing that puts `7e` on screen. */
  card: ReviewCard | null;
  counts: ReviewCounts;
  canUndo: boolean;
  /** Today's two bars. Read in the same closure as `card` and `counts`, so the three describe one
   *  instant — the same reason those two travel together rather than being fetched separately. */
  day: DayProgress;
  /** Present only on the response to an answer that was recorded as **Easy** rather than Good
   *  (日本-700) — a fast Pass on a review card's first showing of the day. The session stamps the
   *  簡 seal over the next card with `revealMs` in it. The rule is the server's; this side only
   *  reports how long the reveal took. */
  promotion?: { revealMs: number } | null;
}

export interface ReviewFinished {
  deck: string;
  reviewed: number;
  passed: number;
  elapsedSeconds: number;
  newSeen: number;
  /** Set when another deck still has cards — which of `7e`'s two states is drawn. */
  nextDeck: string | null;
  nextDeckDue: number;
  dueTomorrow: number;
  /**
   * The deck list as it stands now, which the server computed anyway to pick `nextDeck` (日本-404).
   *
   * **Drawn at the moment the sitting is left, never before.** It is read off the held replica, so
   * it counts this sitting's answers — which is exactly what makes it the right thing to put on
   * screen the instant `完了` is pressed, instead of the pre-sitting figures the landing was still
   * holding. `POST /api/review/end` settles it a moment later with the flushed replica's answer.
   *
   * Applying it as soon as it arrives would be wrong: the reader can `↑` off `7e` and keep
   * answering, and this would then be a landing describing a sitting that is still running.
   */
  landing: ReviewLanding;
}

/** Thrown on a 409: the card moved under us. The caller re-reads rather than retrying. */
export class ReviewConflict extends Error {}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    if (res.status === 409) throw new ReviewConflict(`Request conflicted (${res.status})`);
    // **The server's own words, where it wrote any.** The idle-timeout bounds refuse rather than
    // clamp specifically so the reader is told what happened, and `Request failed (400)` throws
    // that away — the banner then says the app is broken rather than that 999 is not a minute
    // count. `jobs.ts` reads `detail` for the same reason.
    const detail = await res
      .json()
      .then((body) => (body as { detail?: unknown }).detail)
      .catch(() => undefined);
    throw new Error(
      typeof detail === "string" && detail ? detail : `Request failed (${res.status})`,
    );
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

export function reviewLanding(): Promise<ReviewLanding> {
  return request<ReviewLanding>("/api/review/decks");
}

/** Begin or resume a sitting. Idempotent for the deck already open, which is what makes the URL's
 *  `review.deck` enough state to survive a reload. */
export function startReview(deck: string): Promise<ReviewPosition> {
  return post<ReviewPosition>("/api/review/start", { deck });
}

export function reviewPosition(): Promise<ReviewPosition> {
  return request<ReviewPosition>("/api/review/position");
}

/**
 * Grade a card and get the next one.
 *
 * `expectReps` is the card's own `reps` as it was drawn — `notes_bulk_apply`'s `expect` idiom, one
 * integer wide. Answering moves it, so a double-tapped Pass or a second Review tab gets a
 * `ReviewConflict` instead of quietly recording two reviews of one card. **Never retry a conflict
 * blindly**; re-read the position, because that is exactly how one keystroke becomes two reviews.
 *
 * `tookMs` is measured here because only this side knows when the card appeared.
 *
 * `revealMs` is card shown → **first** reveal (日本-700): how long recall took, which is what the
 * Easy promotion is judged on. `tookMs` runs on to the grade and so includes reading the answer.
 * Null when the card was never revealed, which a grade makes impossible — kept nullable so the
 * server's "no figure, no promotion" rule has one input shape.
 */
export function answerCard(
  card: Pick<ReviewCard, "cardId" | "reps">,
  passed: boolean,
  tookMs: number,
  revealMs: number | null = null,
): Promise<ReviewPosition> {
  return post<ReviewPosition>("/api/review/answer", {
    cardId: card.cardId,
    passed,
    expectReps: card.reps,
    tookMs: Math.max(0, Math.round(tookMs)),
    revealMs: revealMs === null ? null : Math.max(0, Math.round(revealMs)),
  });
}

export function undoReview(): Promise<ReviewPosition> {
  return post<ReviewPosition>("/api/review/undo");
}

/** How many new cards まだまだ takes on. One constant, so the button, its title and the request
 *  cannot drift — and so 日本-344 has one place to make it a setting. */
export const EXTEND_NEW_BY = 5;

/**
 * Take on more of today's new cards for one deck, and get `7a` back (日本-451).
 *
 * **`deck` is the deck that HOLDS the cards** — a subdeck row's own `fullName`. The server lifts it
 * and every ancestor of it, because the app takes the tightest per-day limit down the chain: lift
 * the subdeck alone and the landing recomputes to the figure it already had, which reads as a
 * button that does nothing (日本-468).
 *
 * **It answers with the landing**, on `endReview`'s reasoning: a separate `/decks` would queue
 * behind this write on the server's single interactive worker and draw the pre-extension counts.
 *
 * A **409** means a sitting is open in some tab — the write would sync, and a sync clears Anki's
 * undo queue.
 */
export function extendNewLimit(deck: string, by: number = EXTEND_NEW_BY): Promise<ReviewLanding> {
  return post<ReviewLanding>("/api/review/extend", { deck, new: by });
}

export function reviewFinished(): Promise<ReviewFinished> {
  return request<ReviewFinished>("/api/review/finished");
}

/**
 * Leave the sitting, and get `7a` back (日本-404).
 *
 * **This is the exit's only request.** It used to answer 204 and the client separately fetched
 * `/decks` — twice, as it happens — and both of those queued on the server's single interactive
 * worker behind this one's sync flush, so the landing wore the pre-sitting counts for two round
 * trips. The server computes the deck list *after* flushing, inside `end()`, which makes this the
 * freshest answer there is and makes "the landing is read on the flushed replica" a property of the
 * route rather than of the order the client declared its effects in.
 */
export function endReview(): Promise<ReviewLanding> {
  return post<ReviewLanding>("/api/review/end");
}

/**
 * Leave the sitting from a page that is going away.
 *
 * A closed tab never reaches `endReview`, and until something flushes, the session's answers sit in
 * the server's replica and the collection cannot sync. The server's own deadline is the guarantee;
 * this only makes the common case prompt instead of waiting minutes for it.
 *
 * `sendBeacon` because `fetch` from `pagehide` is cancelled with the document. It cannot carry
 * credentials options — it sends the origin's cookies, which is what this needs.
 */
export function endReviewBeacon(): void {
  try {
    navigator.sendBeacon?.(`${API_BASE}/api/review/end`);
  } catch {
    /* a browser without it falls back to the server's deadline, which is the real guarantee */
  }
}

export interface ReviewSettings {
  idleMinutes: number;
  autoplayWord: boolean;
  /** Pass within this many ms of the card appearing (to the reveal) records Easy (日本-700).
   *  **0 is off**, and off is the default. */
  easyThresholdMs: number;
}

export function reviewSettings(): Promise<ReviewSettings> {
  return request<ReviewSettings>("/api/review/settings");
}

/**
 * Save the settings that were passed and leave the rest alone.
 *
 * **A partial body on purpose** — the server reads an absent field as "not supplied" rather than as
 * a reset. That is what lets the panel save one control at a time without each save carrying, and
 * possibly stomping, the value of the other.
 */
export function saveReviewSettings(patch: Partial<ReviewSettings>): Promise<ReviewSettings> {
  return request<ReviewSettings>("/api/review/settings", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export interface DrillCards {
  cards: ReviewCard[];
  autoplayWord: boolean;
}

/**
 * Every card of these notes, drawn as a sitting draws them — for a drill (日本-601).
 *
 * **A read and nothing else.** No sitting is started, no deck is selected and no hold is taken, so
 * a drill cannot reach the scheduler however it is driven. The grading happens in the browser and
 * is thrown away with the run.
 */
export function drillCards(noteIds: number[]): Promise<DrillCards> {
  return post<DrillCards>("/api/review/cards", { noteIds });
}
